import React, { useState, useEffect } from "react";
import { ChakraProvider,} from "@chakra-ui/react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AppProvider } from "./contexts/AppContext";
import "./App.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import BackendLayout from "./pages/BackendLayout";
import Dashboard from "./pages/Dashboard";
import useCustomTheme from "./hooks/useCustomTheme";
import Onboarding from "./pages/Onboarding";
import Funds from "./pages/Funds";
import Library from "./pages/Library";
import Administration from "./pages/Administration";
import Login from "./Login";
import SingleFund from "./components/funds/SingleFund/SingleFund";
import PendingTasks from "./components/tasks/PendingTasks";
import CompletedTasks from "./components/tasks/CompletedTasks";
import ReportingOverview from "./components/reports/ReportingOverview";
import AllReports from "./components/reports/AllReports";
import DocMonitoring from "./components/monitoring/DocMonitoring";
import Screening from "./components/monitoring/Screening";
import InvestmentDetail from "./components/funds/SingleFund/InvestmentDetail";
import InvestorDetail from "./components/funds/SingleFund/InvestorDetail";

function App() {
  const { theme } = useCustomTheme();
  

  // Check if the user is authenticated by looking in localStorage
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const savedAuthState = localStorage.getItem("isAuthenticated");
    const authExpiry = localStorage.getItem("authExpiry");

    // Check if authentication is still valid (expiration not reached)
    if (savedAuthState && authExpiry) {
      const expiryDate = new Date(authExpiry);
      const now = new Date();
      return now < expiryDate;
    }

    return false;
  });

  // Effect to update authentication status in localStorage
  useEffect(() => {
    if (isAuthenticated) {
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 7); // Set expiration to 7 days from now

      localStorage.setItem("isAuthenticated", "true");
      localStorage.setItem("authExpiry", expiryDate.toISOString());
    } else {
      localStorage.removeItem("isAuthenticated");
      localStorage.removeItem("authExpiry");
    }
  }, [isAuthenticated]);

  return (
    <ChakraProvider theme={theme}>
      <AppProvider>
        <Router>
          <ToastContainer
            autoClose={3000}
            hideProgressBar={true}
            closeOnClick
            theme="colored"
            position="bottom-right"
          />
          <AppRoutes
            isAuthenticated={isAuthenticated}
            setIsAuthenticated={setIsAuthenticated}
          />
        </Router>
      </AppProvider>
    </ChakraProvider>
  );
}

export default App;

function AppRoutes({ isAuthenticated, setIsAuthenticated }) {
 
  
  return (
    <Routes>
      {!isAuthenticated ? (
        <Route
          path="*"
          element={<Login setIsAuthenticated={setIsAuthenticated} />}
        />
      ) : (
        <>
          <Route
            path="/"
            element={
              <BackendLayout setIsAuthenticated={setIsAuthenticated}>
                <Dashboard />
              </BackendLayout>
            }
          />
          <Route
            path="/funds/active"
            element={
              <BackendLayout setIsAuthenticated={setIsAuthenticated}>
                <Funds />
              </BackendLayout>
            }
          />

          <Route
            path="funds/:fundName"
            element={
              <BackendLayout setIsAuthenticated={setIsAuthenticated}>
                <SingleFund />
              </BackendLayout>
            }
          />

          <Route path="funds/:fundName/investment/:investmentName" element={
             <BackendLayout setIsAuthenticated={setIsAuthenticated}>
              <InvestmentDetail />
            </BackendLayout>
            }
          />

          <Route path="funds/:fundName/investor/:investorName" element={
             <BackendLayout setIsAuthenticated={setIsAuthenticated}>
              <InvestorDetail />
            </BackendLayout>
            }
          />



          <Route
            path="/funds/onboarding"
            element={
              <BackendLayout setIsAuthenticated={setIsAuthenticated}>
                <Onboarding />
              </BackendLayout>
            }
          />

          <Route
            path="/tasks/pending"
            element={
              <BackendLayout setIsAuthenticated={setIsAuthenticated}>
                <PendingTasks />
              </BackendLayout>
            }
          />

          <Route
            path="/tasks/completed"
            element={
              <BackendLayout setIsAuthenticated={setIsAuthenticated}>
                <CompletedTasks />
              </BackendLayout>
            }
          />
          
          <Route
            path="/monitoring/documents"
            element={
              <BackendLayout setIsAuthenticated={setIsAuthenticated}>
                <DocMonitoring />
              </BackendLayout>
            }
          />
           <Route
            path="/monitoring/screening"
            element={
              <BackendLayout setIsAuthenticated={setIsAuthenticated}>
                <Screening  />
              </BackendLayout>
            }
          />
          <Route
            path="/reporting/overview"
            element={
              <BackendLayout setIsAuthenticated={setIsAuthenticated}>
                <ReportingOverview />
              </BackendLayout>
            }
          />
          <Route
            path="/reporting/all-reports"
            element={
              <BackendLayout setIsAuthenticated={setIsAuthenticated}>
                <AllReports />
              </BackendLayout>
            }
          />
          <Route
            path="/library"
            element={
              <BackendLayout setIsAuthenticated={setIsAuthenticated}>
                <Library />
              </BackendLayout>
            }
          />
          <Route
            path="/administration"
            element={
              <BackendLayout setIsAuthenticated={setIsAuthenticated}>
                <Administration />
              </BackendLayout>
            }
          />

          <Route
            path="/login"
            element={
                <Login setIsAuthenticated={setIsAuthenticated} />
            }
          />
          
          <Route path="*" element={<Navigate to="/" />} />
        </>
      )}
    </Routes>
  );
}

